import {makeAutoObservable, observable} from "mobx";
import type {EditAuthor, KeyedEdit, TaxonomyEdit} from "~/prototypes/pages/taxonomy-canvas/taxonomyEdits.ts";

type EditsState = {
    /** Accepted on the approval page; part of the baseline from then on */
    approved: readonly KeyedEdit[]
    /** Waiting for approval; shown on the approval page */
    pending: readonly KeyedEdit[]
}
const EMPTY: EditsState = {approved: [], pending: []};

/**
 * PROTOTYPE: in-memory edits per taxonomy, shared by the canvas and its approval page so they survive
 * navigation. Nothing is sent to the backend; a page reload clears everything.
 */
class TaxonomyCanvasEditsStore {
    // Shallow: states are replaced as a whole, so array identity changes on every edit
    private readonly byTaxonomy = observable.map<number, EditsState>({}, {deep: false});
    private nextKey = 1;

    constructor() {
        // `get` is a read: keep it a plain function so observers track it (makeAutoObservable would make it an action)
        makeAutoObservable(this, {get: false});
    }

    get(taxonomyId: number): EditsState {
        return this.byTaxonomy.get(taxonomyId) ?? EMPTY;
    }

    add(taxonomyId: number, edit: TaxonomyEdit, author: EditAuthor) {
        const s = this.get(taxonomyId);
        this.byTaxonomy.set(taxonomyId, {...s, pending: [...s.pending, {...edit, key: this.nextKey++, author}]});
    }

    /** Accept one pending edit: it becomes part of the baseline */
    approve(taxonomyId: number, editKey: number) {
        const s = this.get(taxonomyId);
        const edit = s.pending.find(e => e.key === editKey);
        if (!edit) return;
        this.byTaxonomy.set(taxonomyId, {
            approved: [...s.approved, edit],
            pending: s.pending.filter(e => e.key !== editKey),
        });
    }

    /** Undo one pending edit */
    revert(taxonomyId: number, editKey: number) {
        const s = this.get(taxonomyId);
        this.byTaxonomy.set(taxonomyId, {...s, pending: s.pending.filter(e => e.key !== editKey)});
    }

    approveAll(taxonomyId: number) {
        const s = this.get(taxonomyId);
        this.byTaxonomy.set(taxonomyId, {approved: [...s.approved, ...s.pending], pending: []});
    }

    rejectAll(taxonomyId: number) {
        const s = this.get(taxonomyId);
        this.byTaxonomy.set(taxonomyId, {...s, pending: []});
    }
}

export const taxonomyCanvasEditsStore = new TaxonomyCanvasEditsStore();
